"use client"

import { ArrowDownIcon, CheckIcon } from "@heroicons/react/16/solid"
import { BtcIcon, EthIcon, NearIcon, UsdcIcon, UsdtIcon } from "@src/icons"
import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"
import Spinner from "../Spinner"

const data = [
  {
    fromToken: "USDC",
    fromIcon: UsdcIcon,
    fromAmount: "1,025",
    fromUsdAmount: "1,025.00",

    toToken: "NEAR",
    toIcon: NearIcon,
    toAmount: "1,000",
    toUsdAmount: "1,025.00",
  },
  {
    fromToken: "USDT",
    fromIcon: UsdtIcon,
    fromAmount: "6,889",
    fromUsdAmount: "6,889.00",

    toToken: "BTC",
    toIcon: BtcIcon,
    toAmount: "0.1",
    toUsdAmount: "6,889.10",
  },
  {
    fromToken: "ETH",
    fromIcon: EthIcon,
    fromAmount: "5",
    fromUsdAmount: "10,265.00",

    toToken: "BTC",
    toIcon: BtcIcon,
    toAmount: "0.149",
    toUsdAmount: "10,265.00",
  },
  {
    fromToken: "BTC",
    fromIcon: BtcIcon,
    fromAmount: "0.05",
    fromUsdAmount: "3,445.00",

    toToken: "USDC",
    toIcon: UsdcIcon,
    toAmount: "3,445",
    toUsdAmount: "3,445.00",
  },
]

const swapStates = ["Finding best price", "Executing trade", "Trade complete"]

const STATE_DURATION = 3000
const STATES_PER_CARD = swapStates.length

const stackPositions = [
  { y: 0, scale: 1, opacity: 1, zIndex: 3 },
  {
    y: -32,
    scale: 0.9,
    zIndex: 2,
    opacity: 1,
    backgroundColor: "#CCFBEA",
  },
  {
    y: -64,
    scale: 0.8,
    zIndex: 1,
    opacity: 1,
    backgroundColor: "#99F7D5",
  },
  { y: 40, scale: 0.98, opacity: 0, zIndex: 0 },
]

const Swaps = () => {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1)
    }, STATE_DURATION)
    return () => clearInterval(interval)
  }, [])

  const activeIndex = Math.floor(tick / STATES_PER_CARD) % data.length
  const stateIndex = tick % STATES_PER_CARD
  const isComplete = stateIndex === swapStates.length - 1

  return (
    <div className="relative flex w-full justify-center">
      <div className="relative h-60 w-full max-w-[min(90%,288px)]">
        {data.map((card, cardIndex) => {
          const offset = (activeIndex - cardIndex + data.length) % data.length
          const position = stackPositions[offset]
          const isActive = activeIndex === cardIndex
          const FromIcon = card.fromIcon
          const ToIcon = card.toIcon

          return (
            <motion.div
              key={`${card.fromToken}-${card.toToken}`}
              animate={{
                y: position.y,
                scale: position.scale,
                opacity: position.opacity,
                backgroundColor: position.backgroundColor,
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
              style={{
                zIndex: position.zIndex,
              }}
              className={clsx(
                "absolute inset-x-0 bottom-0 origin-bottom rounded-2xl bg-white p-5 outline outline-black/5 transition-shadow",
                isActive ? "shadow-xl" : "outline-transparent"
              )}
            >
              <div className="flex flex-col gap-3 border-gray-200 border-b pb-6">
                <div className="flex items-center gap-3">
                  <FromIcon className="size-9 shrink-0 rounded-full" />
                  <div className="flex flex-col gap-0.5">
                    <div className="font-bold text-gray-900 text-sm/4 tracking-tight">
                      {card.fromAmount} {card.fromToken}
                    </div>
                    <div className="font-medium text-gray-500 text-xs/3">
                      ${card.fromUsdAmount}
                    </div>
                  </div>
                </div>

                <div className="flex w-9 items-center justify-center">
                  <ArrowDownIcon className="size-4 text-gray-400" />
                </div>

                <div className="flex items-center gap-3">
                  <ToIcon className="size-9 shrink-0 rounded-full" />
                  <div className="flex flex-col gap-0.5">
                    <div className="font-bold text-gray-900 text-sm/4 tracking-tight">
                      {card.toAmount} {card.toToken}
                    </div>
                    <div className="font-medium text-gray-500 text-xs/3">
                      ${card.toUsdAmount}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 overflow-hidden pt-5">
                <div
                  className={clsx(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-white transition-colors duration-150",
                    isComplete || !isActive ? "bg-green-600" : "bg-sky-400"
                  )}
                >
                  <AnimatePresence mode="popLayout">
                    {isComplete || !isActive ? (
                      <motion.div
                        key="check"
                        initial={{
                          opacity: 0,
                          scale: 0.7,
                          filter: "blur(4px)",
                        }}
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        transition={{ duration: 0.3 }}
                      >
                        <CheckIcon className="size-4" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="spinner"
                        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        exit={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
                        transition={{ duration: 0.3 }}
                      >
                        <Spinner size="sm" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={isActive ? stateIndex : swapStates.length - 1}
                    initial={{ y: 5, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -5, opacity: 0 }}
                    transition={{
                      y: { type: "spring", stiffness: 750, damping: 30 },
                      opacity: { duration: 0.15 },
                    }}
                    className={clsx(
                      "flex-1 font-semibold text-xs transition-colors duration-150",
                      isComplete || !isActive
                        ? "text-green-600"
                        : "text-sky-400"
                    )}
                  >
                    {isActive
                      ? swapStates[stateIndex]
                      : swapStates[swapStates.length - 1]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

export default Swaps
